import { useState } from 'react'
import { useParams } from 'react-router'
import { Cpu, HardDrive, IdCard, MemoryStick, MonitorSmartphone, type LucideIcon } from 'lucide-react'
import { quotaTiers as api, users as userApi } from '@/api/endpoints'
import type { QuotaTier, QuotaTierPatch, UserList } from '@/api/types'
import { Err } from '@/components/Err'
import { Facts } from '@/components/Facts'
import { InlineEdit } from '@/components/InlineEdit'
import { Pager } from '@/components/Pager'
import { ToneBadge } from '@/components/ToneBadge'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { limitAxes, tierDraftOf, tierLimitsBody, type TierDraft } from '@/domain/quota'
import { FREE } from '@/domain/words'
import { peopleChanged, usePage } from '@/hooks/usePage'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useWorldRefresh } from '@/hooks/useWorld'
import { QuotaPanel } from './user/QuotaPanel'
import { CHIP } from './vm/chips'
import { opensDetail, personRow, teamsOf } from './model'
import type { World } from './model'
import { PeopleTable } from './PeopleTable'
import { TierEditForm } from './QuotaTierForms'

const POLL_MS = 30000
// 멤버는 사람의 목록이라 사용자 목록과 같은 간격이다 — 변경 알림(최고 관리자)과 쓰기 뒤의 다시 읽기가 그 사이를 메운다.
const MEMBERS_POLL_MS = 8000
const NO_ONE: UserList = { items: [], total: 0 }

// 직위 상세 — 그 직위의 한도와 그 직위에 든 멤버. 목록에서 줄을 누르면 온다.
// **값은 두 길로 고친다**(F-123): 그 자리에서(`InlineEdit` — 이름은 제목을, 한도는 축의 카드를 누른다), 그리고 제목 줄의
// `수정` 이 여는 창에서 이름과 네 축을 한 번에(`QuotaTierForms.tsx` 의 `TierEditForm`). 그 자리 고치기만으로는 고칠 수 있다는
// 것이 보이지 않았다. 한도는 한 사람이 가진 모든 VM 의 합계라 절 제목이 `한 사람당 한도` 다.
// 한도의 저장은 네 축 전체를 싣는다: `limits` 를 실은 PATCH 는 다섯 축을 통째로 바꾸므로 한 축만 보내면 나머지가 자유가 된다.
// 누가 그 직위인지는 사람 쪽의 값(`User.quota_tier`)이라 사람을 넣는 것은 관리자의 사용자 목록의 창이다.
// 멤버는 서버가 이 직위로 좁힌 계정 목록의 쪽이다(`GET /users` 의 `tier`, 탈퇴한 사람은 빼고) — 모든 사람을 받아 거르지 않는다.
// 멤버의 칸은 사용자 목록과 같다(`PeopleTable`). `사용자 추가` 는 사용자 목록의 `직위` 창을 이 직위가 골라진 채로 연다 —
// 같은 일을 두 자리에서 다른 창으로 하지 않게.
// **권한은 한도와 다른 절이다**(F-126) — `노드 선택` 은 숫자가 아니라 할 수 있는 일이라 스위치이고, 누르는 것이 곧 저장이다.
// 같은 값을 `수정` 창의 체크도 고친다(두 길을 함께 둔다).

/** 한도 카드의 축 — 순서와 글자는 목록의 칸(`limitAxes`)과 같다. 칸의 단위는 치는 수의 단위다. */
const AXES: { key: keyof TierDraft; icon: LucideIcon; unit: string }[] = [
  { key: 'vms', icon: MonitorSmartphone, unit: '대' },
  { key: 'cores', icon: Cpu, unit: '코어' },
  { key: 'memoryGb', icon: MemoryStick, unit: 'GB' },
  { key: 'diskGb', icon: HardDrive, unit: 'GB' },
]

export default function QuotaTierView({ world }: { world: World }) {
  const { id } = useParams()
  // 경로의 직위가 바뀌면 처음부터 그린다 — 앞 직위의 오류·열린 창·고치던 값이 다음 직위에 남지 않는다.
  return <TierDetail key={id} id={id} world={world} />
}

function TierDetail({ id, world }: { id: string | undefined; world: World }) {
  const me = useSession()
  const tiers = usePoll(() => api.list(), POLL_MS)
  const refreshWorld = useWorldRefresh()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const tier = (tiers.data ?? []).find((t) => t.id === id)
  const members = usePage({
    read: ({ limit, offset }) => (id ? userApi.page({ tier: id, withdrawn: false, limit, offset }) : Promise.resolve(NO_ONE)),
    terms: [],
    filters: id,
    match: peopleChanged,
    pollMs: MEMBERS_POLL_MS,
  })

  if (!tier) return tiers.loading ? <Empty>불러오는 중</Empty> : <Empty>없는 직위</Empty>

  // 계정 줄은 소속을 싣지 않는다 — 팀 칸은 `World` 의 팀 목록에서 잇는다.
  const rows = (members.data?.items ?? []).map((u) => personRow(u, world.teams))
  // 직위의 이름은 사용자 목록의 직위 칸에도 서므로 사람 목록도 다시 읽는다.
  const patch = async (body: QuotaTierPatch) => {
    await api.patch(tier.id, body)
    tiers.refresh()
    refreshWorld()
  }
  const makeDefault = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.setDefault(tier.id)
      tiers.refresh()
    } catch (err) {
      setError(err)
    }
    setBusy(false)
  }

  return (
    <>
      <Crumb steps={[{ label: '직위', to: '/quota-tiers' }, { label: tier.display_name }]} />
      <PageHead
        icon={<IdCard className="size-5" />}
        kind="직위"
        title={
          <InlineEdit
            label="이름"
            value={tier.display_name}
            display={tier.display_name}
            required
            onSave={(v) => patch({ display_name: v })}
          />
        }
        afterTitle={tier.is_default ? <ToneBadge tone="ok">기본</ToneBadge> : undefined}
        facts={`${tier.people}명`}
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
            <button type="button" className={CHIP} onClick={() => setEditing(true)}>
              수정
            </button>
            <button type="button" className={CHIP} onClick={() => setAdding(true)}>
              사용자 추가
            </button>
            <button type="button" className={CHIP} disabled={tier.is_default || busy} onClick={() => void makeDefault()}>
              기본 지정
            </button>
          </div>
        }
      />

      <Err error={tiers.error} />
      <Err error={error} />

      <Section title="한 사람당 한도">
        <LimitCards tier={tier} onSave={(limits) => patch({ limits })} />
        <div className="mt-3">
          <Facts
            rows={[]}
            expert={[
              [
                '순서',
                <InlineEdit
                  key="order"
                  label="순서"
                  value={String(tier.sort_order)}
                  display={<span className="tabular-nums">{tier.sort_order}</span>}
                  numeric
                  required
                  onSave={(v) => patch({ sort_order: Number(v) })}
                />,
              ],
            ]}
          />
        </div>
      </Section>

      <Section title="권한">
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
          <Label htmlFor="tier-node-choice">노드 선택</Label>
          <Switch
            id="tier-node-choice"
            checked={tier.node_choice}
            disabled={busy}
            onCheckedChange={(v) => {
              setBusy(true)
              setError(null)
              patch({ node_choice: v })
                .catch((err: unknown) => setError(err))
                .finally(() => setBusy(false))
            }}
          />
        </div>
      </Section>

      <Section title={members.data ? `멤버 ${members.data.total ?? rows.length}` : '멤버'}>
        <Err error={members.error} />
        {rows.length === 0 ? (
          members.data ? <Empty>멤버 없음</Empty> : members.loading ? <Empty>불러오는 중</Empty> : null
        ) : (
          <>
            <PeopleTable
              group={rows}
              last={{ head: '팀', cell: (p) => teamsOf(p).map((t) => t.display_name).join(', ') }}
              opens={(p) => opensDetail(world, me, p.user.id)}
            />
            <Pager page={members.page} pages={members.pages} onPage={members.setPage} className="pt-3" />
          </>
        )}
      </Section>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent aria-describedby={undefined}>
          {editing ? (
            <TierEditForm
              tier={tier}
              onDone={() => {
                setEditing(false)
                tiers.refresh()
                refreshWorld()
              }}
              onClose={() => setEditing(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-3xl">
          {adding ? (
            <QuotaPanel
              seed={[]}
              teams={world.teams}
              tier={tier.id}
              onDone={() => setAdding(false)}
              onApplied={tiers.refresh}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * 한도의 네 축을 한 장씩. 카드의 값을 누르면 그 자리가 칸이 되고, 비우면 자유다(placeholder 가 말한다).
 * 저장은 지금 네 축에 고친 한 축을 얹은 전체다(`tierDraftOf` → `tierLimitsBody`).
 */
function LimitCards({ tier, onSave }: { tier: QuotaTier; onSave: (limits: ReturnType<typeof tierLimitsBody>) => Promise<unknown> }) {
  const draft = tierDraftOf(tier.limits)
  const shown = limitAxes(tier.limits)
  return (
    <div className="grid grid-cols-4 gap-3 max-sm:grid-cols-2">
      {AXES.map((a, i) => {
        const [word, text] = shown[i] ?? ['', '']
        const Icon = a.icon
        return (
          <div key={a.key} className="grid content-start gap-1.5 rounded-lg border bg-card px-4 py-3">
            <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <Icon className="size-3.5" aria-hidden />
              {word}
            </span>
            <InlineEdit
              label={`${word} 한도`}
              value={draft[a.key]}
              display={
                <span className={text === FREE ? 'text-[18px] font-medium text-muted-foreground' : 'text-[18px] font-semibold tabular-nums'}>
                  {text}
                </span>
              }
              placeholder={FREE}
              suffix={a.unit}
              numeric
              onSave={(v) => onSave(tierLimitsBody({ ...draft, [a.key]: v }))}
            />
          </div>
        )
      })}
    </div>
  )
}
